// Jira's values as the chosen token sees them: projects and labels as the user types (after a
// short pause), and the issue types and statuses of every chosen project, merged.
import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { jiraFieldsQuery, jiraLabelsQuery, jiraProjectsQuery } from "../api";

const PAUSE_MS = 250;

export function useDebounced<T>(value: T, ms = PAUSE_MS): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export function useJiraProjects(credentialId: string, typed: string) {
  return useQuery(jiraProjectsQuery(credentialId, useDebounced(typed)));
}

export function useJiraLabels(credentialId: string, typed: string) {
  return useQuery(jiraLabelsQuery(credentialId, useDebounced(typed)));
}

export interface ProjectFields {
  issueTypes: string[];
  statuses: string[];
  /** Whether every project's fields have been read. */
  ready: boolean;
  error: Error | null;
}

const unique = (names: string[]) => [...new Set(names)];

export function useProjectFields(credentialId: string, projects: string[]): ProjectFields {
  const results = useQueries({ queries: projects.map((p) => jiraFieldsQuery(credentialId, p)) });
  const data = results.flatMap((r) => (r.data ? [r.data] : []));
  return {
    issueTypes: unique(data.flatMap((d) => d.issue_types.map((t) => t.name))),
    statuses: unique(data.flatMap((d) => d.statuses.map((s) => s.name))),
    ready: credentialId !== "" && projects.length > 0 && results.every((r) => r.isSuccess),
    error: results.find((r) => r.error)?.error ?? null,
  };
}
